import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { useCostCenters } from "@/hooks/useCostCenters";
import { unitBelongsToCostCenter } from "@/pages/Gerencial/ChaoDeObra/chaoUnitCardUtils";
import { fetchAllUnitsForRegistry, type ComercialUnitRow } from "@/services/comercial";
import {
  apiError,
  createCasa,
  createColaborador,
  createProblema,
  linkCentroCusto,
  patchColaborador,
  removeColaborador,
  saveDenominacoes,
  savePonto,
  unlinkCentroCusto,
  type ManutencaoSnapshot,
  type PontoMarca,
} from "@/services/manutencao";
import { CARGOS, CATEGORIAS, StatusBadge, casaLabel, fieldClass, formatDay } from "./shared";

type AdminTab = "equipe" | "ponto" | "relatorios" | "config";

export function AdminPanels({
  tab,
  data,
  reload,
}: {
  tab: AdminTab;
  data: ManutencaoSnapshot;
  reload: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      await reload();
    } catch (error) {
      setMsg(apiError(error, "Não foi possível salvar."));
    } finally {
      setBusy(false);
    }
  }

  if (tab === "equipe") return <Equipe data={data} busy={busy} msg={msg} onRun={run} />;
  if (tab === "ponto") return <Ponto data={data} busy={busy} msg={msg} onRun={run} />;
  if (tab === "config") return <Config data={data} busy={busy} msg={msg} onRun={run} />;
  return <Relatorios data={data} />;
}

function Equipe({
  data,
  busy,
  msg,
  onRun,
}: {
  data: ManutencaoSnapshot;
  busy: boolean;
  msg: string | null;
  onRun: (action: () => Promise<void>) => Promise<void>;
}) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [cargo, setCargo] = useState<string>(CARGOS[5]);
  const [diaria, setDiaria] = useState("0");

  return (
    <section className="space-y-4">
      <form
        className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-5"
        onSubmit={(e) => {
          e.preventDefault();
          void onRun(async () => {
            await createColaborador({ nome, email, cargo, valorDiaria: Number(diaria) || 0 });
            setNome("");
            setEmail("");
          });
        }}
      >
        <input className={fieldClass()} placeholder="Nome" value={nome} onChange={(e) => setNome(e.target.value)} required />
        <input className={fieldClass()} placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
        <select className={fieldClass()} value={cargo} onChange={(e) => setCargo(e.target.value)}>
          {CARGOS.map((item) => <option key={item}>{item}</option>)}
        </select>
        <input className={fieldClass()} type="number" min="0" step="0.01" placeholder="Diária" value={diaria} onChange={(e) => setDiaria(e.target.value)} />
        <Button type="submit" loading={busy}>Incluir</Button>
      </form>
      {msg && <p className="text-sm text-red-600">{msg}</p>}
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Cargo</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Diária</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {data.colaboradores.map((item) => (
              <tr key={item.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-800">{item.nome}<div className="text-xs text-slate-400">{item.email}</div></td>
                <td className="px-4 py-3">
                  <select className={fieldClass()} value={item.cargo} onChange={(e) => void onRun(() => patchColaborador(item.id, { cargo: e.target.value }))}>
                    {CARGOS.map((cargoItem) => <option key={cargoItem}>{cargoItem}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <button type="button" className="text-xs font-semibold text-brand-700" onClick={() => void onRun(() => patchColaborador(item.id, { status: item.status === "aprovado" ? "pendente" : "aprovado" }))}>
                    {item.status === "aprovado" ? "Aprovado" : "Pendente"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <input
                    className={fieldClass()}
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={item.valorDiaria}
                    onBlur={(e) => void onRun(() => patchColaborador(item.id, { valorDiaria: Number(e.target.value) || 0 }))}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <button type="button" className="text-xs font-semibold text-red-600" onClick={() => { if (window.confirm(`Remover ${item.nome}?`)) void onRun(() => removeColaborador(item.id)); }}>Remover</button>
                </td>
              </tr>
            ))}
            {data.colaboradores.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Nenhum colaborador na equipe de manutenção.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const PROXIMA_MARCA: Record<string, PontoMarca | null> = { "": "P", P: "F", F: "G", G: null };
const MARCA_CLASS: Record<PontoMarca, string> = {
  P: "bg-emerald-100 text-emerald-800",
  F: "bg-red-100 text-red-800",
  G: "bg-sky-100 text-sky-800",
};

function Ponto({
  data,
  busy,
  msg,
  onRun,
}: {
  data: ManutencaoSnapshot;
  busy: boolean;
  msg: string | null;
  onRun: (action: () => Promise<void>) => Promise<void>;
}) {
  const [mesOffset, setMesOffset] = useState(0);
  const base = useMemo(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + mesOffset, 1);
  }, [mesOffset]);
  const dias = useMemo(() => {
    const total = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    return Array.from({ length: total }, (_, index) => {
      const day = index + 1;
      return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    });
  }, [base]);
  const pessoas = data.colaboradores.filter((c) => c.status === "aprovado" && c.cargo !== "ADM");
  const label = base.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" onClick={() => setMesOffset((v) => v - 1)}>Mês anterior</Button>
        <p className="min-w-40 text-center text-lg font-bold capitalize text-brand-700">{label}</p>
        <Button type="button" variant="secondary" onClick={() => setMesOffset((v) => v + 1)}>Próximo mês</Button>
      </div>
      <div className="flex flex-wrap gap-2 text-xs font-semibold">
        <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">P = Presente</span>
        <span className="rounded-full bg-red-100 px-3 py-1 text-red-800">F = Falta</span>
        <span className="rounded-full bg-sky-100 px-3 py-1 text-sky-800">G = Folga</span>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-500">— = Não registrado</span>
      </div>
      <p className="text-sm text-slate-500">Clique na célula para alternar P, F e G. A marcação grava na hora.</p>
      {msg && <p className="text-sm text-red-600">{msg}</p>}
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full border-collapse text-xs">
          <thead>
            <tr className="bg-slate-800 text-white">
              <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-left">Colaborador</th>
              {dias.map((day) => {
                const date = new Date(`${day}T00:00:00`);
                const weekend = date.getDay() === 0 || date.getDay() === 6;
                return (
                  <th key={day} className={`px-1 py-1 text-center font-medium ${weekend ? "bg-slate-700" : ""}`}>
                    <div className="text-[10px] opacity-80">{["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"][date.getDay()]}</div>
                    <div className="text-sm font-bold">{date.getDate()}</div>
                  </th>
                );
              })}
              <th className="px-2">P</th>
              <th className="px-2">F</th>
            </tr>
          </thead>
          <tbody>
            {pessoas.map((pessoa) => {
              const marcas = dias.map((day) => data.pontos.find((p) => p.colaboradorNome === pessoa.nome && p.data === day)?.marca);
              return (
                <tr key={pessoa.id} className="border-t border-slate-100">
                  <td className="sticky left-0 bg-white px-3 py-2 font-semibold text-slate-800">{pessoa.nome}</td>
                  {dias.map((day, index) => {
                    const marca = marcas[index];
                    const next = PROXIMA_MARCA[marca ?? ""] ?? "P";
                    return (
                      <td key={day} className="p-1 text-center">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void onRun(() => savePonto(pessoa.nome, day, next))}
                          className={`h-9 w-9 rounded-lg text-xs font-bold ${marca ? MARCA_CLASS[marca] : "bg-slate-50 text-slate-400"}`}
                        >
                          {marca ?? "—"}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-2 text-center font-semibold text-emerald-700">{marcas.filter((m) => m === "P").length}</td>
                  <td className="px-2 text-center font-semibold text-red-700">{marcas.filter((m) => m === "F").length}</td>
                </tr>
              );
            })}
            {pessoas.length === 0 && (
              <tr><td colSpan={dias.length + 3} className="px-4 py-8 text-center text-sm text-slate-500">Cadastre a equipe aprovada para lançar o ponto.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Relatorios({ data }: { data: ManutencaoSnapshot }) {
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [obra, setObra] = useState("");
  const items = data.agendamentos.filter((a) => {
    if (de && a.data < de) return false;
    if (ate && a.data > ate) return false;
    if (obra && a.empreendimento !== obra) return false;
    return true;
  });
  const counts = {
    concluido: items.filter((a) => a.status === "concluido").length,
    ausente: items.filter((a) => a.status === "ausente").length,
    parcial: items.filter((a) => a.status === "parcial").length,
    cancelado: items.filter((a) => a.status === "cancelado").length,
    livre: data.atividades.filter((a) => (!de || a.data >= de) && (!ate || a.data <= ate)).length,
  };
  const abc = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of items) {
      for (const problema of item.problemas) map.set(problema, (map.get(problema) ?? 0) + 1);
    }
    const rows = [...map.entries()].sort((a, b) => b[1] - a[1]);
    const total = rows.reduce((sum, row) => sum + row[1], 0) || 1;
    let acc = 0;
    return rows.map(([nome, qtd]) => {
      acc += qtd;
      const pct = acc / total;
      const classe = pct <= 0.8 ? "A" : pct <= 0.95 ? "B" : "C";
      return { nome, qtd, classe };
    });
  }, [items]);

  const custos = useMemo(() => {
    const dias = new Map<string, Set<string>>();
    for (const apt of items) {
      if (!["concluido", "parcial", "ausente", "atendimento"].includes(apt.status)) continue;
      for (const nome of apt.tecnicos) {
        const key = `${apt.empreendimento || "Sem obra"}|${nome}|${apt.data}`;
        if (!dias.has(apt.empreendimento || "Sem obra")) dias.set(apt.empreendimento || "Sem obra", new Set());
        dias.get(apt.empreendimento || "Sem obra")!.add(`${nome}|${apt.data}`);
        void key;
      }
    }
    return [...dias.entries()].map(([obraNome, set]) => {
      let valor = 0;
      for (const token of set) {
        const nome = token.split("|")[0];
        const pessoa = data.colaboradores.find((c) => c.nome === nome);
        valor += pessoa?.valorDiaria ?? 0;
      }
      return { obraNome, diarias: set.size, valor };
    });
  }, [items, data.colaboradores]);

  return (
    <section className="space-y-4">
      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-4">
        <input type="date" className={fieldClass()} value={de} onChange={(e) => setDe(e.target.value)} />
        <input type="date" className={fieldClass()} value={ate} onChange={(e) => setAte(e.target.value)} />
        <select className={fieldClass()} value={obra} onChange={(e) => setObra(e.target.value)}>
          <option value="">Todos os centros de custo</option>
          {data.empreendimentos.map((item) => <option key={item.id}>{item.nome}</option>)}
        </select>
        <button type="button" className="text-sm font-semibold text-brand-700" onClick={() => window.print()}>Imprimir / PDF</button>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {[
          ["Concluídos", counts.concluido],
          ["Cliente ausente", counts.ausente],
          ["Parciais", counts.parcial],
          ["Cancelados", counts.cancelado],
          ["Ativ. avulsas", counts.livre],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><th className="px-4 py-3">Data</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Obra</th><th className="px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-slate-100">
                <td className="px-4 py-3">{formatDay(item.data)} {item.hora}</td>
                <td className="px-4 py-3 font-medium">{item.cliente}</td>
                <td className="px-4 py-3">{item.empreendimento || "—"}</td>
                <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-bold uppercase text-slate-500">Curva ABC — defeitos</h3>
          {abc.length === 0 && <p className="text-sm text-slate-500">Sem ocorrências no filtro.</p>}
          <ul className="space-y-2">
            {abc.map((row) => (
              <li key={row.nome} className="flex items-center justify-between text-sm">
                <span>{row.nome}</span>
                <span className="font-semibold">{row.qtd} · {row.classe}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-bold uppercase text-slate-500">Centro de custos por obra</h3>
          {custos.length === 0 && <p className="text-sm text-slate-500">Uma diária por colaborador em cada dia trabalhado.</p>}
          <ul className="space-y-2 text-sm">
            {custos.map((row) => (
              <li key={row.obraNome} className="flex justify-between">
                <span>{row.obraNome}</span>
                <span className="font-semibold">{row.diarias} diária{row.diarias === 1 ? "" : "s"} · {row.valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function compareUnits(a: ComercialUnitRow, b: ComercialUnitRow): number {
  const quadra = (a.constructiveUnitName || "").localeCompare(b.constructiveUnitName || "", "pt-BR", { numeric: true });
  if (quadra !== 0) return quadra;
  return a.name.localeCompare(b.name, "pt-BR", { numeric: true });
}

function Config({
  data,
  busy,
  msg,
  onRun,
}: {
  data: ManutencaoSnapshot;
  busy: boolean;
  msg: string | null;
  onRun: (action: () => Promise<void>) => Promise<void>;
}) {
  const { options: centros, loading: loadingCentros, error: erroCentros } = useCostCenters();
  const [empId, setEmpId] = useState(data.empreendimentos[0]?.id ?? "");
  const [ccId, setCcId] = useState("");
  const [buscaCc, setBuscaCc] = useState("");
  const [units, setUnits] = useState<ComercialUnitRow[]>([]);
  const [unitsState, setUnitsState] = useState<"loading" | "ready" | "error">("loading");
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [nomes, setNomes] = useState<Record<string, string>>({});
  const [buscaCasa, setBuscaCasa] = useState("");
  const [numero, setNumero] = useState("");
  const [quadra, setQuadra] = useState("");
  const [categoria, setCategoria] = useState(CATEGORIAS[0].nome);
  const [item, setItem] = useState("");

  const emp = data.empreendimentos.find((e) => e.id === empId) ?? null;
  const sienge = emp?.origem === "sienge";

  useEffect(() => {
    let cancel = false;
    setUnitsState("loading");
    fetchAllUnitsForRegistry()
      .then((list) => {
        if (cancel) return;
        setUnits(list);
        setUnitsState("ready");
      })
      .catch((error) => {
        if (cancel) return;
        setUnitsError(apiError(error, "Não foi possível carregar as unidades do Sienge."));
        setUnitsState("error");
      });
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    if (!empId) return;
    const draft: Record<string, string> = {};
    for (const casa of data.casas) {
      if (casa.empreendimentoId === empId && casa.siengeUnitId) draft[casa.siengeUnitId] = casa.numero;
    }
    setNomes(draft);
    setBuscaCasa("");
  }, [empId, data.casas]);

  const unidades = useMemo(() => {
    if (!sienge || !emp) return [];
    return units.filter((unit) => unitBelongsToCostCenter(unit, emp.id)).sort(compareUnits);
  }, [sienge, emp, units]);

  const unidadesFiltradas = useMemo(() => {
    const q = buscaCasa.trim().toLowerCase();
    if (!q) return unidades;
    return unidades.filter((unit) => {
      const nome = nomes[unit.id] ?? "";
      return `${unit.name} ${unit.constructiveUnitName} ${nome}`.toLowerCase().includes(q);
    });
  }, [unidades, buscaCasa, nomes]);

  const centrosFiltrados = centros.filter((option) => {
    const q = buscaCc.trim().toLowerCase();
    return !q || option.label.toLowerCase().includes(q);
  });

  function numerarCasas() {
    const preenchido = Object.values(nomes).some((value) => value.trim());
    if (preenchido && !window.confirm("Substituir os nomes já preenchidos por Casa 01, Casa 02…?")) return;
    const next: Record<string, string> = {};
    unidades.forEach((unit, index) => {
      next[unit.id] = `Casa ${String(index + 1).padStart(2, "0")}`;
    });
    setNomes(next);
  }

  return (
    <section className="grid gap-4 lg:grid-cols-3">
      {msg && <p className="text-sm text-red-600 lg:col-span-3">{msg}</p>}
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="font-semibold text-slate-900">Centros de custo</h3>
        <p className="text-xs text-slate-500">Cadastro do Sienge. Inclua o centro que será usado na manutenção.</p>
        <input className={fieldClass()} placeholder="Buscar centro de custo" value={buscaCc} onChange={(e) => setBuscaCc(e.target.value)} />
        <select className={fieldClass()} value={ccId} onChange={(e) => setCcId(e.target.value)} disabled={loadingCentros}>
          <option value="">{loadingCentros ? "Carregando centros de custo…" : "Selecione no Sienge…"}</option>
          {centrosFiltrados.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        {erroCentros && <p className="text-sm text-red-600">{erroCentros}</p>}
        <Button
          type="button"
          loading={busy}
          disabled={!ccId}
          onClick={() => {
            const option = centros.find((item) => item.value === ccId);
            if (!option) return;
            void onRun(async () => {
              await linkCentroCusto(option.value, option.label);
              setEmpId(option.value);
              setCcId("");
            });
          }}
        >
          Incluir centro
        </Button>
        <ul className="max-h-64 space-y-1 overflow-y-auto text-sm">
          {data.empreendimentos.map((centro) => {
            const qtd = units.filter((unit) => unitBelongsToCostCenter(unit, centro.id)).length;
            const ativo = centro.id === empId;
            return (
              <li key={centro.id} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${ativo ? "bg-brand-50 text-brand-800" : "text-slate-600"}`}>
                <button type="button" className="min-w-0 flex-1 truncate text-left" onClick={() => setEmpId(centro.id)}>
                  {centro.nome}
                  {centro.origem === "sienge"
                    ? <span className="ml-1 text-xs text-slate-400">{unitsState === "ready" ? `· ${qtd} un.` : ""}</span>
                    : <span className="ml-1 text-xs text-slate-400">· cadastro local</span>}
                </button>
                <button
                  type="button"
                  className="shrink-0 text-xs font-semibold text-red-600"
                  onClick={() => {
                    if (window.confirm(`Remover ${centro.nome} da manutenção? As denominações das casas deste centro também saem.`)) {
                      void onRun(async () => {
                        await unlinkCentroCusto(centro.id);
                        if (empId === centro.id) setEmpId(data.empreendimentos.find((item) => item.id !== centro.id)?.id ?? "");
                      });
                    }
                  }}
                >
                  Remover
                </button>
              </li>
            );
          })}
          {data.empreendimentos.length === 0 && <li className="text-slate-500">Nenhum centro de custo incluído.</li>}
        </ul>
      </div>
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="font-semibold text-slate-900">Casas</h3>
        <select className={fieldClass()} value={empId} onChange={(e) => setEmpId(e.target.value)}>
          <option value="">Selecione o centro de custo…</option>
          {data.empreendimentos.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </select>
        {sienge && (
          <>
            <p className="text-xs text-slate-500">
              As unidades vêm do Sienge. Aqui você denomina cada uma: Casa 01, Casa 02, Apto 03…
            </p>
            <input className={fieldClass()} placeholder="Buscar unidade ou nome" value={buscaCasa} onChange={(e) => setBuscaCasa(e.target.value)} />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" disabled={unidades.length === 0 || busy} onClick={numerarCasas}>
                Denominar Casa 01, 02…
              </Button>
              <Button
                type="button"
                loading={busy}
                disabled={!emp || unidades.length === 0}
                onClick={() => {
                  if (!emp) return;
                  void onRun(() => saveDenominacoes(emp.id, unidades.map((unit) => ({
                    siengeUnitId: unit.id,
                    denominacao: (nomes[unit.id] ?? "").trim(),
                    quadra: unit.constructiveUnitName || unit.name,
                    grupo: unit.constructiveUnitName || "Unidades Sienge",
                  }))));
                }}
              >
                Salvar nomes
              </Button>
            </div>
            {unitsState === "loading" && <p className="text-sm text-slate-500">Carregando unidades do Sienge…</p>}
            {unitsState === "error" && <p className="text-sm text-red-600">{unitsError}</p>}
            {unitsState === "ready" && unidades.length === 0 && (
              <p className="text-sm text-slate-500">Nenhuma unidade do Sienge vinculada a este centro de custo.</p>
            )}
            <ul className="max-h-80 space-y-2 overflow-y-auto">
              {unidadesFiltradas.map((unit) => (
                <li key={unit.id} className="grid gap-1 rounded-lg border border-slate-100 p-2">
                  <p className="truncate text-xs text-slate-500" title={unit.name}>
                    Sienge: {unit.name}{unit.constructiveUnitName ? ` · ${unit.constructiveUnitName}` : ""}
                  </p>
                  <input
                    className={fieldClass()}
                    placeholder="Casa 01"
                    value={nomes[unit.id] ?? ""}
                    onChange={(e) => setNomes((prev) => ({ ...prev, [unit.id]: e.target.value }))}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
        {!sienge && emp && (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void onRun(async () => { await createCasa(emp.id, numero, quadra); setNumero(""); setQuadra(""); }); }}>
            <p className="text-xs text-slate-500">Cadastro local antigo. Centros do Sienge usam as unidades da API.</p>
            <input className={fieldClass()} placeholder="Número ou nome" value={numero} onChange={(e) => setNumero(e.target.value)} required />
            <input className={fieldClass()} placeholder="Quadra / lote" value={quadra} onChange={(e) => setQuadra(e.target.value)} />
            <Button type="submit" loading={busy}>Salvar casa</Button>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-slate-600">
              {data.casas.filter((c) => c.empreendimentoId === emp.id).map((c) => (
                <li key={c.id}>{casaLabel(c.numero)} · {c.quadra || "sem quadra"}</li>
              ))}
            </ul>
          </form>
        )}
        {!emp && <p className="text-sm text-slate-500">Inclua um centro de custo para denominar as casas.</p>}
      </div>
      <form className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4" onSubmit={(e) => { e.preventDefault(); void onRun(async () => { await createProblema(categoria, item); setItem(""); }); }}>
        <h3 className="font-semibold text-slate-900">Itens de problema</h3>
        <select className={fieldClass()} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
          {CATEGORIAS.map((c) => <option key={c.nome}>{c.nome}</option>)}
        </select>
        <input className={fieldClass()} placeholder="Novo item" value={item} onChange={(e) => setItem(e.target.value)} required />
        <Button type="submit" loading={busy}>Salvar item</Button>
        <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-slate-600">
          {data.problemas.map((p) => <li key={p.id}>{p.categoria} → {p.item}</li>)}
        </ul>
      </form>
    </section>
  );
}
